import { useQuery } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { ContentfulCurrentActiveEntries } from '@jbhi-fi/apis/src/contentful/types';

export type UseContentfulCurrentWebsiteElementsProps = {
  contentfulThemeApiClient?: ContentfulThemeApiClient;
};

type ContentfulCurrentActiveEntriesResult = Omit<ContentfulCurrentActiveEntries['fields'], 'name' | 'snackbar'>;

export const useContentfulCurrentWebsiteElements = (props: UseContentfulCurrentWebsiteElementsProps = {}) => {
  const { contentfulThemeApiClient: contentfulThemeApiClientContext } = usePublicApiClient();
  const contentfulThemeApiClient = props.contentfulThemeApiClient || contentfulThemeApiClientContext;
  if (!contentfulThemeApiClient) {
    throw new Error(
      'useContentfulCurrentWebsiteElements must be provided with a contentfulThemeApiClient via PublicApiClientProvider or props'
    );
  }

  const { isLoading, data: activeEntryData } = useQuery({
    enabled: !!contentfulThemeApiClient,
    queryKey: ['useContentfulCurrentWebsiteElements', 'currentActiveEntry'],
    refetchOnWindowFocus: false,
    queryFn: () =>
      contentfulThemeApiClient?.getEntries({ content_type: 'currentActiveHomepage', include: '0', cache: 'medium' }),
  });
  const currentActiveEntry = activeEntryData?.data?.items?.[0] as ContentfulCurrentActiveEntries;
  const currentActiveEntryFields = currentActiveEntry?.fields as ContentfulCurrentActiveEntriesResult;
  const currentWebsiteEntryIds = currentActiveEntryFields
    ? (Object.fromEntries(
        Object.entries(currentActiveEntryFields)
          .filter(([, value]) => value.sys?.id)
          .map(([key, value]) => [key, value.sys.id] as const)
      ) as Partial<Record<keyof ContentfulCurrentActiveEntriesResult, string>>)
    : undefined;
  return { isLoading, currentWebsiteEntryIds };
};
